import { useMemo, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { ApiClientProvider } from "../api/ApiClientContext.js";
import { createApiClient } from "../api/client.js";
import { SessionExpiryContext, type SessionExpiryChannel } from "../auth/sessionExpiry.js";
import { AuthProvider } from "../auth/AuthContext.js";
import { LanUnlockPage } from "../auth/LanUnlockPage.js";
import type { Capabilities } from "../api/capabilities.js";
import { SetupWizard } from "../desktop/SetupWizard.js";
import { useDesktopSettings } from "../desktop/useDesktopSettings.js";
import { useCapabilities } from "../hooks/useCapabilities.js";
import { AppRoutes } from "./AppRoutes.js";
import "./App.css";

export interface AppProps {
  /**
   * API origin. Empty in production (same-origin `/api/...`, see
   * `client.ts`'s doc comment); tests point this at the ephemeral server
   * started by `test-support/server.ts`. Assumes the caller already wraps
   * `<App>` in a Router (`BrowserRouter` in `main.tsx`, `MemoryRouter` in
   * tests) — this component does not provide one itself, so tests can
   * control the initial route.
   */
  apiBaseUrl?: string;
}

export function App({ apiBaseUrl = "" }: AppProps) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: false }, mutations: { retry: false } },
      }),
  );
  // Filled in by `AuthProvider` once it mounts; see `auth/sessionExpiry.ts`
  // for why the client cannot simply close over auth state.
  const [expiryChannel] = useState<SessionExpiryChannel>(() => ({ onExpired: null }));
  const apiClient = useMemo(
    () => createApiClient(apiBaseUrl, { onUnauthorized: () => expiryChannel.onExpired?.() }),
    [apiBaseUrl, expiryChannel],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <SessionExpiryContext.Provider value={expiryChannel}>
        <ApiClientProvider client={apiClient}>
          <CapabilitiesBoundary />
        </ApiClientProvider>
      </SessionExpiryContext.Provider>
    </QueryClientProvider>
  );
}

/** Waits for `GET /api/capabilities` before rendering anything else — the whole shell (auth, routes, printing) branches on it. */
function CapabilitiesBoundary() {
  const { data: capabilities, isPending, isError, error, refetch } = useCapabilities();

  if (isPending) {
    return (
      <div className="app-boundary-state" role="status">
        Nalaganje …
      </div>
    );
  }

  if (isError) {
    return (
      <div className="app-boundary-state" role="alert">
        <p>Povezava s strežnikom ni uspela.</p>
        <p>{error instanceof Error ? error.message : String(error)}</p>
        <button type="button" onClick={() => void refetch()}>
          Poskusi znova
        </button>
      </div>
    );
  }

  // A desktop LAN device that has not yet entered the shared passphrase: gate
  // the whole shell behind the unlock screen. Sits *outside* `AuthProvider`
  // because desktop has no accounts to provide — the passphrase, not a session,
  // is what this client is missing. Unlocking re-fetches capabilities (now
  // `locked: false`), which drops through to the normal app below. The till
  // (loopback) is never locked, so it never sees this.
  if (capabilities.lan?.locked) {
    return <LanUnlockPage onUnlocked={() => void refetch()} />;
  }

  return <DesktopSetupBoundary capabilities={capabilities} />;
}

/**
 * On the desktop shell, a fresh install has not been set up yet — hold the whole
 * app behind the first-run wizard until it has (`window.desktop` settings'
 * `setupComplete`). On the hosted deployment, or a LAN browser with no shell,
 * there is no bridge and this drops straight through. Sits outside
 * `AuthProvider` for the same reason the LAN unlock screen does: desktop has no
 * accounts, and this is about the machine, not a session.
 */
function DesktopSetupBoundary({ capabilities }: { capabilities: Capabilities }) {
  const { bridge, settings, isPending, update } = useDesktopSettings();

  if (capabilities.deployment !== "desktop" || bridge === null) {
    return <AuthedApp capabilities={capabilities} />;
  }

  if (isPending || settings === undefined) {
    return (
      <div className="app-boundary-state" role="status">
        Nalaganje …
      </div>
    );
  }

  if (!settings.setupComplete) {
    return <SetupWizard bridge={bridge} initial={settings} persist={update} />;
  }

  return <AuthedApp capabilities={capabilities} />;
}

function AuthedApp({ capabilities }: { capabilities: Capabilities }) {
  return (
    <AuthProvider capabilities={capabilities}>
      <AppRoutes capabilities={capabilities} />
    </AuthProvider>
  );
}
